import React, { useContext, useState } from 'react'
import { useHistory, Redirect } from 'react-router-dom'
import { NavLink } from 'react-router-dom'
import { CONFIRMED, PALO_BLUE, PATHS, SUBMITTED, BLOCKED } from '../../constants'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import MenuIcon from '@mui/icons-material/Menu'
import LogoutIcon from '@mui/icons-material/Logout'
import SettingsIcon from '@mui/icons-material/Settings'
import MenuItem from '@mui/material/MenuItem'
import ListItemText from '@mui/material/ListItemText'
import ListItemIcon from '@mui/material/ListItemIcon'
import VpnKeyOutlinedIcon from '@mui/icons-material/VpnKeyOutlined';
import SupervisedUserCircleOutlinedIcon from '@mui/icons-material/SupervisedUserCircleOutlined';
import { Typography } from '@mui/material'
import { createTheme, ThemeProvider } from '@mui/material/styles'
import { authenticationService } from '../../Utilities/authenticationService'
import { useStickyState } from '../../Utilities/useStickyState'
import {
    AccountCircleOutlined,
    EventNoteOutlined,
    HomeOutlined,
    AutoGraphOutlined,
    SendOutlined,
    EditOutlined,
    SearchOutlined,
    CalendarToday,
    KeyboardArrowLeft,
    KeyboardArrowRight
} from '@mui/icons-material'
import { SwipeableDrawer, Drawer, Divider, List, BottomNavigation, BottomNavigationAction, Snackbar, Alert } from '@mui/material'
import { EmploymentsContext } from '../../contexts/EmploymentsContext'
import ButtonWithConfirmPrompt from '../ButtonWithConfirmPrompt'
import { isMobile } from '../../Utilities/isMobile'

const drawerWidth = 240

export const theme = createTheme({
    palette: {
        paloBlue: {
            main: PALO_BLUE,
            contrastText: '#fff'
        },
        success: {
            main: '#4CAF50',
            contrastText: '#fff'
        },
        submitted: {
            main: SUBMITTED,
            contrastText: '#fff'
        },
        confirmed: {
            main: CONFIRMED,
            contrastText: '#fff'
        },
        blocked: {
            main: BLOCKED,
            contrastText: '#fff'
        }
    },
    components: {
        MuiAutocomplete: {
            defaultProps: {
                noOptionsText: '該当なし'
            }
        }
    }
})

const DrawerItem = ({ toggleDrawer, path, text, icon, isExternal = false, isHighlighted = false }) => {
    if (isExternal) {
        return (
            <MenuItem 
                onClick={() => {
                    toggleDrawer(false);
                    window.location.href = 'https://ringerhutschedule.com/home';
                }}
                sx={isHighlighted ? {
                    backgroundColor: '#FF9800',
                    color: 'white',
                    '&:hover': {
                        backgroundColor: '#F57C00',
                    },
                    '& .MuiListItemIcon-root': {
                        color: 'white'
                    },
                    '& .MuiListItemText-primary': {
                        fontWeight: 'bold'
                    }
                } : {}}
            >
                <ListItemIcon sx={{ mr: '1rem' }}>{icon}</ListItemIcon>
                <ListItemText primary={text} />
            </MenuItem>
        )
    }
    return (
        <NavLink to={path} aria-label={text} onClick={() => toggleDrawer(false)}>
            <MenuItem>
                <ListItemIcon sx={{ mr: '1rem' }}>{icon}</ListItemIcon>
                <ListItemText primary={text} />
            </MenuItem>
        </NavLink>
    )
}

const Layout = ({ children, testMode=false }) => {
    const history = useHistory()
    const [open, setOpen] = useState(false)
    const [desktopMenuOpen, setDesktopMenuOpen] = useStickyState(true, 'frederickDesktopMenuOpen')
    const { isManager, canEditShift, setUserEmployments, isAdmin, resetAppsStates } = useContext(EmploymentsContext)
    const [value, setValue] = React.useState(0);
    const isLoggedIn = localStorage.getItem('currentUser') !== null
    const calendarYearMonth = JSON.parse(localStorage.getItem('frederickSelectedYearMonth'))
    const [snackbar, setSnackbar] = React.useState({}) // { message: string, severity: string success|error|warning|info }
    const currentUser = authenticationService.currentUserValue
    const viewOnly = currentUser?.view_only

    const toggleDrawer = action => {
        setOpen(action)
        setDesktopMenuOpen(action)
    }

    const handleLogout = () => {
        setUserEmployments([])
        resetAppsStates()
        localStorage.clear()
        authenticationService.logout()
        history.push(PATHS.login)
    }

    const toolbar = (
        <>
            <Box sx={{display: 'flex', justifyContent: 'space-between', width: '100%'}}>
                <img src="/company-logo.png" alt="company-logo" style={{ width: drawerWidth, height: 'auto' }} onClick={() => history.push(PATHS.home)}/>
                <IconButton
                    size="large"
                    edge="start"
                    color="inherit"
                    aria-label="menu"
                    sx={{ mr: 2 }}
                    onClick={() => toggleDrawer(true)}
                >
                    <MenuIcon />
                </IconButton>
            </Box>
            <SwipeableDrawer
                id="drawer"
                anchor="left"
                open={open}
                onClose={() => toggleDrawer(false)}
                onOpen={() => toggleDrawer(true)}
            >
                <List>
                    <DrawerItem toggleDrawer={toggleDrawer} path={PATHS.home} icon={<HomeOutlined />} text="ホーム" />
                </List>

                {!viewOnly && (<>
                    <Divider orientation="horizontal" flexItem />
                    <List>
                        {(isManager || canEditShift) && (
                            <DrawerItem
                                toggleDrawer={toggleDrawer}
                                path={PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'create')}
                                icon={<EditOutlined />}
                                text="シフト表を作成する"
                            />
                        )}
                        <DrawerItem
                            toggleDrawer={toggleDrawer}
                            path={PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'check')}
                            icon={<EventNoteOutlined />}
                            text="シフトを確認する"
                        />
                        <DrawerItem
                            toggleDrawer={toggleDrawer}
                            path={PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'submit')}
                            icon={<SendOutlined />}
                            text="希望シフトを提出する"
                        />
                    </List>
                </>)}
                <Divider orientation="horizontal" flexItem />
                <List>
                    <DrawerItem
                        toggleDrawer={toggleDrawer}
                        path={PATHS.userSettings}
                        icon={<AccountCircleOutlined />}
                        text="あなたのアカウント"
                    />
                    {isManager && (
                        <DrawerItem
                            toggleDrawer={toggleDrawer}
                            path={PATHS.managerSettingsUsers}
                            icon={<SettingsIcon />}
                            text="設定"
                        />
                    )}
                    <ButtonWithConfirmPrompt
                        component={
                            <MenuItem aria-label="logout">
                                <ListItemIcon sx={{ mr: '1rem' }}>
                                    <LogoutIcon />
                                </ListItemIcon>
                                <ListItemText primary="ログアウト" />
                            </MenuItem>
                        }
                        title="ログアウト"
                        content="ログアウトしてよろしいですか？"
                        confirmText="ログアウト"
                        handleConfirm={() => {
                            toggleDrawer(false)
                            handleLogout()
                        }}
                    />
                </List>
            </SwipeableDrawer>
        </>
    )

    const desktopMenu = (
        <>
            <Drawer variant="permanent" open={desktopMenuOpen}>   
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '100vh',
                    width: desktopMenuOpen ? '100%' : '3.5rem'
                }}>
                    <List>
                        {desktopMenuOpen ? <img src="/company-logo.png" alt="company-logo" style={{ margin: '.5rem', width: 230, height: 'auto' }} onClick={() => history.push(PATHS.home)}/> 
                        : <img src="/company-logo-collasped.png" alt="company-logo" style={{ margin: '.5rem', width: '2.5rem', height: 'auto' }} onClick={() => history.push(PATHS.home)}/>}

                        <MenuItem
                            onClick={() => history.push(PATHS.home)}
                            sx={{ fontSize: '1rem' }}
                        >
                            <HomeOutlined sx={{ mr: '1rem' }} />
                            {desktopMenuOpen && <Typography>ホーム</Typography>}
                        </MenuItem>
                        <MenuItem
                            onClick={() => history.push(PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'check'))}
                            sx={{ fontSize: '1rem' }}
                        >
                            <EventNoteOutlined sx={{ mr: '1rem' }} />
                            {desktopMenuOpen && <Typography>シフト管理</Typography>}
                        </MenuItem>
                        {isManager && (<>
                            <MenuItem onClick={() => history.push(PATHS.managerSettingsUsers)} sx={{ fontSize: '1rem' }}>
                                <SupervisedUserCircleOutlinedIcon sx={{ mr: '1rem' }}/>
                                {desktopMenuOpen && <Typography>ユーザー・店舗情報</Typography>}
                            </MenuItem>
                        </>)}
                        {isManager && (
                            <MenuItem
                                onClick={() => history.push(PATHS.feedbackManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth))}
                                sx={{ fontSize: '1rem' }}
                            >
                                <AutoGraphOutlined sx={{ mr: '1rem' }} />
                                {desktopMenuOpen && <Typography>総合評価グラフ</Typography>}
                            </MenuItem>
                        )}
                        {isAdmin && (
                            <MenuItem onClick={() => history.push(PATHS.adminUserManagements)} sx={{ fontSize: '1rem' }}>
                                <VpnKeyOutlinedIcon sx={{ mr: '1rem' }}/>
                                {desktopMenuOpen && <Typography>管理者設定</Typography>}
                            </MenuItem>
                        )}
                    </List>
                    <List sx={{ pb: 0 }}>
                        <MenuItem
                            onClick={() => history.push(PATHS.userSettings)}
                            sx={{ fontSize: '1rem' }}
                        >
                            <AccountCircleOutlined sx={{ mr: '1rem' }} />
                            {desktopMenuOpen && <Typography>あなたのアカウント</Typography>}
                        </MenuItem>
                        <ButtonWithConfirmPrompt
                            component={
                                <MenuItem aria-label="logout">
                                    <LogoutIcon sx={{ mr: '1rem' }}/>
                                    {desktopMenuOpen && <Typography>ログアウト</Typography>}
                                </MenuItem>
                            }
                            title="ログアウト"
                            content="ログアウトしてよろしいですか？"
                            confirmText="ログアウト"
                            handleConfirm={() => {
                                toggleDrawer(false)
                                handleLogout()
                            }}
                        />
                        <Box sx={{ backgroundColor: '#1976D2', mb: 0, display: 'flex', justifyContent: 'space-evenly' }}>
                            {desktopMenuOpen ?
                                <IconButton
                                    onClick={() => setDesktopMenuOpen(false)}
                                >
                                    <KeyboardArrowLeft sx={{color: 'white'}}/>
                                </IconButton>
                                :
                                <IconButton
                                    onClick={() => setDesktopMenuOpen(true)}
                                >
                                    <KeyboardArrowRight sx={{color: 'white'}}/>
                                </IconButton>
                            }
                        </Box>
                    </List>
                </Box>
            </Drawer>
        </>
    )
    
    const mobileBottomNavigation = <>
        {localStorage.getItem('selected_emp') && 
            <Box sx={{ width: '100vw', position: 'fixed', bottom: 0, zIndex: 1000, boxShadow: '0px -2px 4px rgba(0, 0, 0, 0.1)' }}>
                <BottomNavigation
                    showLabels
                    value={value}
                    onChange={(event, newValue) => {
                        history.push(newValue)
                        setValue(newValue);
                    }}
                    sx={{ width: '100%', position: 'sticky', bottom: 0, backgroundColor: 'white'}}
                >
                    <BottomNavigationAction label="ホーム" icon={<HomeOutlined />} value={PATHS.home}/>
                    {!viewOnly && <BottomNavigationAction label="シフト" icon={<CalendarToday />} value={PATHS.shiftManagement.replace(':employmentId', localStorage.selected_emp).replace(':calendarYearMonth', calendarYearMonth).replace(':tabValue', 'check')}/>}
                    {(isManager || isAdmin) && <BottomNavigationAction label="検索" icon={<SearchOutlined />} value={PATHS.managerSettingsUsers}/>}
                    {/* {isAdmin && <BottomNavigationAction label="管理者" icon={<VpnKeyOutlined />} value={PATHS.adminUserManagements}/>} */}
                    <BottomNavigationAction
                        label="アカウント"
                        icon={<AccountCircleOutlined />}
                        sx={{
                            '& .MuiBottomNavigationAction-label': {
                                fontSize: '0.75rem',
                                whiteSpace: 'nowrap',
                            }
                        }}
                        value={PATHS.userSettings}
                    />
                </BottomNavigation>
            </Box>
        }
    </>

    if (!currentUser && !testMode) {
        // not logged in so redirect to login page with the return url
        return (
            <Redirect
                to={{
                    pathname: PATHS.login,
                }}
            />
        )
    }
    
    return (
        <ThemeProvider theme={theme}>
            <Box sx={{ flexGrow: 1 }}>
                    {!isMobile() ? (
                        localStorage.getItem('currentUser') == null ? null : (
                            desktopMenu
                        )
                    ) : (
                        <AppBar position="static" color="">
                            {localStorage.getItem('currentUser') == null ? null : toolbar}
                            {mobileBottomNavigation}
                        </AppBar>
                    )}
                    <div style={{marginLeft: isMobile() ? 0 : isLoggedIn ? desktopMenuOpen ? '250px' : '3rem' : 0}}>
                        <Snackbar
                            open={snackbar.message?.length > 0}
                            autoHideDuration={10000}
                            onClose={() => setSnackbar({})}
                            anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                        >
                            <Alert severity={snackbar.severity || 'success'} onClose={() => setSnackbar({})}>
                                {snackbar.message}
                            </Alert>
                        </Snackbar>
                        {children}
                    </div>
            </Box>
        </ThemeProvider>
    )
}

export default Layout
